import adaptiveBezierCurve from 'adaptive-bezier-curve'
import bezier from 'bezier'

const cubicBezierCurve = bezier.prepare(4)

const DEFAULT_OPTIONS = {
  bezierNumPoints: 0, // If omitted/falsy, will use adaptive-bezier-curve. Otherwise will use a bezier function that outputs a fixed length of points
  // computeTotalLength: false // If true, will compute the 2D path length in user units (px?) similar to SVGElement.getTotalLength()
}

/**
 * 2D Canvas-like drawing context that outputs a path as a series of 2D points.
 * - Also calculates path length (similar to the getTotalLength() function on SVG Elements)
 */

class PathDrawingContext {
  constructor(canvas2DRenderingContext, options = DEFAULT_OPTIONS) {
    this._ctx = canvas2DRenderingContext // Support drawing to an actual 2D rendering context
    this._bezierNumPoints = options.bezierNumPoints
    this._points = []
    this._totalLen = 0

    this._pointsReceiver = {
      // Hijack points array push calls to continually calculate estimated total length
      push: xy => {
        const points = this._points
        if (points.length > 0) {
          const prevXY = points[points.length - 1]
          const dx = xy[0] - prevXY[0]
          const dy = xy[1] - prevXY[1]
          // Add linear distance to new point
          this._totalLen += Math.sqrt(dx * dx + dy * dy)
        }
        points.push(xy)
      }
    }
  }

  moveTo(x, y) {
    if (this._points.length && !this._ctx) {
      throw new Error('PathDrawingContext: moveTo only supported at start of path when in no-context mode (no 2D rendering context supplied)')
    }
    this._pointsReceiver.push([x, y])

    this._ctx && this._ctx.moveTo(x, y)
  }

  closePath() {
    const points = this._points
    if (points.length > 1) {
      this._pointsReceiver.push(points[0].slice())
    }
    this._ctx && this._ctx.closePath()
  }

  lineTo(x, y) {
    this._pointsReceiver.push([x, y])
    this._ctx && this._ctx.lineTo(x, y)
  }

  bezierCurveTo(x1, y1, x2, y2, x, y) {
    const points = this._points
    const startPoint = points[points.length - 1]
    if (this._bezierNumPoints) {
      // Fixed-precision curve (number of output points)
      const xPoints = [startPoint[0], x1, x2, x]
      const yPoints = [startPoint[1], y1, y2, y]
      // Start with idx=1 (t!=0), to avoid duplicate points
      for (let i = 1; i < this._bezierNumPoints + 1; i++) {
        const t = i * (1 / this._bezierNumPoints)
        this._pointsReceiver.push([
          cubicBezierCurve(xPoints, t),
          cubicBezierCurve(yPoints, t)
        ])
      }
    }
    else {
      adaptiveBezierCurve(
        startPoint,
        [x1, y1],
        [x2, y2],
        [x, y],
        1.0,
        this._pointsReceiver
      )
    }
    this._ctx && this._ctx.bezierCurveTo(x1, y1, x2, y2, x, y)
  }

  getPoints() {
    return this._points
  }

  getTotalLength() {
    return this._totalLen
  }
}

export default PathDrawingContext
